import {get, orderBy, startCase, toLower, toNumber, toUpper} from 'lodash-es';
import {BUY_SELL_FLAG_MAPPER, ORDER_TYPES, TRANSACTION_TYPE} from '../../../constants/equity/orderForm/oneTime';
import commonBaseService from '../../commonBaseService';
import {ltpFeedService} from '../ltpFeedService';
import stocksPortfolioPnlService from './stocksPortfolioPnlService';
import orderScripService from '../orderForm/orderScripService';
import orderFormService from '../orderForm/orderFormService';
import {ORDER_MODE, PRODUCT_TYPE_ID, SEGMENT_ID_MAPPER, TRANSACTION_TYPES} from '../../../constants/equity/common';
import numberValidityService from "../orderForm/numberValidityService";

function getSortedHoldingsList(order = 'asc', key = 'display_name', holdings = []) {
	if (commonBaseService.isEmptyOrUndefined(holdings) || commonBaseService.isEmptyOrUndefined(order)) {
		return holdings;
	}
	return orderBy(
		holdings,
		[
			(obj) => {
				const value = get(obj, key);
				return key === 'display_name' ? value : toNumber(value);
			},
		],
		[order],
	);
}

const _isCarryforwardExists = (cf_quantity) => {
	return commonBaseService.isValidNumber(Number(cf_quantity));
};

const _calculatePrevDayClose = (ltp, one_day_change) => {
	const lastTradePrice = Number(ltp);
	const oneDayChange = Number(one_day_change);
	if (commonBaseService.isValidNumber(lastTradePrice) && commonBaseService.isValidNumber(oneDayChange)) {
		return lastTradePrice - oneDayChange;
	}

	return '-';
};

const calculatePositionsTodaysInvested = ({
	cf_quantity,
	one_day_change,
	buy_quantity,
	avg_buy_price,
	sell_quantity,
	avg_sell_price,
	last_trade_price,
}) => {
	const prevDayClose = _calculatePrevDayClose(last_trade_price, one_day_change);
	const buyInvested = stocksPortfolioPnlService.calculateInvestedValue(buy_quantity, avg_buy_price);
	const sellInvested = stocksPortfolioPnlService.calculateInvestedValue(sell_quantity, avg_sell_price);
	const isValidCarryforward = _isCarryforwardExists(cf_quantity);
	let cfInvested = 0;
	if (isValidCarryforward) {
		cfInvested = stocksPortfolioPnlService.calculateInvestedValue(cf_quantity, prevDayClose);
	}

	const isValidInvestedAmounts =
		commonBaseService.isValidNumber(buyInvested) &&
		commonBaseService.isValidNumber(sellInvested) &&
		commonBaseService.isValidNumber(cfInvested);
	if (!isValidInvestedAmounts) {
		return '-';
	} else {
		return cfInvested + buyInvested - sellInvested;
	}
};



const getAveragePrice = (overall_net_quantity, overall_buy_avg_price, overall_sell_avg_price) => {
	if (overall_net_quantity > 0) {
		return overall_buy_avg_price;
	} else if (overall_net_quantity < 0) {
		return overall_sell_avg_price;
	} else {
		return 0;
	}
};

export const getCalculatedHoldingsListItemData = (apiData, feedMsg) => {
	const { dp_quantity: quantity = '', dp_buy_price: averagePrice = '' } = apiData;
	const { last_trade_price, divider, one_day_change, netchangefromprevclose } = feedMsg;
	const ltp = ltpFeedService.formatLtpValue(last_trade_price, divider);
	const _invested = stocksPortfolioPnlService.calculateInvestedValue(quantity, averagePrice);
	const _current = stocksPortfolioPnlService.calculateCurrentValue(quantity, ltp);
	const _todaysPnl = stocksPortfolioPnlService.calculateTodaysPnl(quantity, one_day_change);
	const _overallPnl = stocksPortfolioPnlService.calculateOverAllPnl(_current, _invested);
	const _overallPnlPercentage = stocksPortfolioPnlService.calculateOverAllPnlPercentage(_current, _invested);

	const updatedObj = {
		...apiData,
		_invested,
		_current,
		_todaysPnl,
		_overallPnl,
		_overallPnlPercentage,
		ltp,
		netchangefromprevclose,
	};

	return updatedObj;
};

const getTokenData = (token, socketDataMap, apiData) => {
	if (socketDataMap.has(token)) {
		return socketDataMap.get(token);
	} else {
		const data = apiData.find((scrip) => {
			// nse_token, bse_token for holdings
			// scrip_token for positions
			const scripToken = scrip.nse_token || scrip.bse_token || scrip.scrip_token;
			return scripToken == token;
		});
		return data;
	}
};

const getCalculatedPositionListItemData = (scripData, feedMsg) => {
	const {
		cf_quantity,
		cf_avg_price,
		buy_quantity,
		avg_buy_price,
		sell_quantity,
		avg_sell_price,
		net_quantity,
		instrument,
		lot_size,
	} = scripData;

	const isCurrency = orderScripService.isCurrencyDerivatesScrip(instrument);
	const { last_trade_price, divider, netchangefromprevclose } = feedMsg;
	const ltp = ltpFeedService.formatLtpValue(last_trade_price, divider, isCurrency);
	const _overallPnl = stocksPortfolioPnlService.calculatePositionsOverallPnl(
		cf_quantity,
		cf_avg_price,
		buy_quantity,
		avg_buy_price,
		sell_quantity,
		avg_sell_price,
		ltp,
		lot_size,
		isCurrency,
	);
	const _current = stocksPortfolioPnlService.calculateCurrentValue(net_quantity, ltp);

	const updateScripData = {
		...scripData,
		_overallPnl,
		_current,
		ltp,
		netchangefromprevclose,
	};

	return updateScripData;
};

const isPositionOpen = (net_quantity) => {
	const positionQuantity = Number(net_quantity);
	if (commonBaseService.isValidNumber(positionQuantity)) {
		return positionQuantity !== 0;
	}
	return true;
};

const createPositionId = ( exchange, scrip_token, product_type ) => {
	if (
		!commonBaseService.isEmptyOrUndefined(exchange) &&
		!commonBaseService.isEmptyOrUndefined(scrip_token) &&
		!commonBaseService.isEmptyOrUndefined(product_type)
	) {
		return `${exchange}_${scrip_token}_${product_type?.toUpperCase()}`;
	}
	return '';
};

const getAbsoluteNetQuantity = (net_quantity) => {
	let absoluteQuantity;
	if (!commonBaseService.isEmptyOrUndefined(net_quantity)) {
		absoluteQuantity = Math.abs(Number(net_quantity));
	}
	if (commonBaseService.isValidNumber(absoluteQuantity)) {
		return absoluteQuantity;
	}
	return 0;
};

const getProductTypeLabel = (productType, instrument) => {
	const isFno = orderScripService.isCarryForwardProduct(instrument);
	if (productType === 'DELIVERY' && isFno) {
		return 'Carryforward';
	} else if (productType === 'MTF') {
		return 'MTF';
	} else if (productType === 'BTST') {
		return 'Delivery Plus';
	} else {
		return startCase(toLower(productType));
	}
};


const getOpenPositions = (allPositions = {}) => {
	const openPositions = {};
	Object.entries(allPositions).forEach(([key, value]) => {
		if (value.overall_net_quantity != 0) {
			openPositions[key] = value;
		}
	});
	return openPositions;
};

const createInitialMarketOrderData = (quantity) => {
	const orderFormData = {
		order_type: ORDER_TYPES.market,
		quantity: Math.abs(quantity),
	};
	return orderFormData;
};

const getConvertPositionBottomsheetTitle = (productType) => {
	const CONVERT_POSITION_BT_TITLE_MAPPER = {
		Delivery: {
			value: 'Delivery to Intraday',
		},
		Intraday: {
			value: 'Intraday to Delivery',
		},
	};
	return `Convert position: ${CONVERT_POSITION_BT_TITLE_MAPPER[productType]?.value}`;
};

const getConvertPositionBodyText = (scripName, quantity, productType) => {
	const CONVERT_POSITION_BODY_TEXT_MAPPER = {
		Delivery: {
			value: `Your ${scripName} Delivery position of ${quantity} Qty will be converted into Intraday Position. Do you wish to continue?`,
		},
		Intraday: {
			value: `Your ${scripName} Intraday position of ${quantity} Qty will be converted into Delivery Position. Do you wish to continue?`,
		},
	};
	return CONVERT_POSITION_BODY_TEXT_MAPPER[productType]?.value;
};

const getConvertPositionButtonTitle = (productType, isOpenPosition) => {
	if (commonBaseService.isEmptyOrUndefined(productType)) {
		return '';
	}
	if (isOpenPosition) {
		if (productType === 'Delivery') {
			return 'Convert position to Intraday';
		} else if (productType === 'Intraday') {
			return 'Convert position to Delivery';
		} else {
			return '';
		}
	}
};

const getNewProductType = (productType) => {
	if (orderFormService.isDeliveryOrder(productType)) {
		return 'INTRADAY';
	} else {
		return 'DELIVERY';
	}
};

const getHoldingsSortData = (isOverallPnl) => {
	const pnlTitle = isOverallPnl ? 'By overall P&L' : 'By today’s P&L';
	const HOLDINGS_SORT_DATA = [
		{
			title: 'By name',
			contents: [
				{
					label: 'A to Z',
					key: 'display_name',
					order: 'asc',
					value: 'display_name_asc',
					eventKey: 'by name',
				},
				{
					label: 'Z to A',
					key: 'display_name',
					order: 'desc',
					value: 'display_name_desc',
					eventKey: 'by name',
				},
			],
		},
		{
			title: 'By current value',
			contents: [
				{
					label: 'High to low',
					value: 'current_desc',
					key: '_current',
					order: 'desc',
					eventKey: 'by current value',
				},
				{
					label: 'Low to high',
					value: 'current_asc',
					key: '_current',
					order: 'asc',
					eventKey: 'by current value',
				},
			],
		},
		{
			title: pnlTitle,
			contents: [
				{
					label: 'High to low',
					value: 'pnl_desc',
					key: 'pnl',
					order: 'desc',
					eventKey: 'by p&l',
				},
				{
					label: 'Low to high',
					value: 'pnl_asc',
					key: 'pnl',
					order: 'asc',
					eventKey: 'by p&l',
				},
			],
		},
	];

	return HOLDINGS_SORT_DATA;
};

function deliveryT1InfoConfig(productType, transactionType, exchange) {
	let transactionPrice = '';
	let infoText = '';
	let autoRedirect = true;
	if (orderFormService.isDeliveryOrder(productType) && orderFormService.isEquityOrderByExchange(exchange)) {
		const friendlyTransactionType = orderFormService.isSellTransaction(transactionType) ? 'sold' : 'bought';
		infoText = `Shares ${friendlyTransactionType} today appear in positions. Your holdings quantity will be updated tomorrow.`;
		autoRedirect = false;
	}
	return {
		transactionPrice,
		infoText,
		autoRedirect,
	};
}

function deliveryT1InfoConfigByQuantity(productType, exchange, quantity) {
	if (isPositionOpen(quantity)) {
		const transactionType = quantity > 0 ? TRANSACTION_TYPE.BUY : TRANSACTION_TYPE.SELL;
		return deliveryT1InfoConfig(productType, transactionType, exchange);
	}
	return {};
}

const getDeliveryT1TagConfig = (productType, exchange, netQuantity) => {
	const qty = Number(netQuantity);
	let color = 'content.secondary';
	let tag = '';
	if (orderFormService.isDeliveryOrder(productType) && orderFormService.isEquityOrderByExchange(exchange)) {
		if (qty > 0) {
			color = 'secondary.profitGreen.400';
			tag = 'Holdings';
		} else if (qty < 0) {
			color = 'secondary.lossRed.400';
			tag = 'Sold Holdings';
		}
	}

	return {
		color,
		tag,
	};
};

const getProductConversionConfig = (selectedPositionDetails, ltp, divider) => {
	const {
		overall_net_quantity,
		product_type,
		overall_buy_avg_price,
		overall_sell_avg_price,
		exchange,
		scrip_token,
	} = selectedPositionDetails;
	const newProductType = stocksPortfolioService.getNewProductType(product_type);
	const isQuantityPositive = overall_net_quantity > 0;
	const isIntradayToDelivery = orderFormService.isIntradayOrder(product_type) && orderFormService.isDeliveryOrder(newProductType);
	const absoluteQuantity = Math.abs(overall_net_quantity);
	const overallAvg = isQuantityPositive ? overall_buy_avg_price : overall_sell_avg_price;
	const transactionType = isQuantityPositive ? TRANSACTION_TYPES.BUY : TRANSACTION_TYPES.SELL;
	const transactionTypeFlag = BUY_SELL_FLAG_MAPPER[transactionType];
	const isSellOrder = orderFormService.isSellTransaction(transactionType);

	return {
		productType: product_type,
		absoluteQuantity,
		overallNetQuantity: overall_net_quantity,
		overallAvg,
		transactionType,
		transactionTypeFlag,
		newProductType,
		exchange,
		marketSegmentId: SEGMENT_ID_MAPPER[exchange],
		scripToken: scrip_token,
		displayName: selectedPositionDetails.display_name,
		isIntradayToDelivery,
		isSellOrder,
		shouldCheckEdis: isIntradayToDelivery && isSellOrder,
		ltp,
		divider,
		isin: selectedPositionDetails.isin,
	};
};

const getMarginInfoPlayLoad = (productConversionConfig) => {
	return {
		NoOfLegs: 1,
		Mode: ORDER_MODE.MODIFY,
		LegDetails: [
			{
				LegNo: 1,
				BuyOrSell: productConversionConfig.transactionTypeFlag,
				MarketSegmentId: productConversionConfig.marketSegmentId,
				Token: productConversionConfig.scripToken,
				Quantity: productConversionConfig.absoluteQuantity,
				Price: productConversionConfig.ltp,
				MktFlag: 0,
				OldPrice: numberValidityService.convertRupeesToPaisa(productConversionConfig.overallAvg, productConversionConfig.divider) || 0,
				OldQuantity: productConversionConfig.absoluteQuantity,
				ProductType: PRODUCT_TYPE_ID[productConversionConfig.newProductType],
				LegIndicator: 0,
			},
		],
	};
};

const getConvertPositionPayload = (productConversionConfig) => {
	return {
		exchange: productConversionConfig.exchange,
		scrip_token: productConversionConfig.scripToken,
		transaction_type: productConversionConfig.transactionType,
		quantity: productConversionConfig.absoluteQuantity,
		old_product_type: toUpper(productConversionConfig.productType),
		new_product_type: productConversionConfig.newProductType,
		bo_order_id: '',
	};
};

const stocksPortfolioService = {
	getSortedHoldingsList,
	getAveragePrice,
	getCalculatedHoldingsListItemData,
	getTokenData,
	getCalculatedPositionListItemData,
	isPositionOpen,
	createPositionId,
	getAbsoluteNetQuantity,
	getProductTypeLabel,
	calculatePositionsTodaysInvested,
	getOpenPositions,
	createInitialMarketOrderData,
	getConvertPositionBottomsheetTitle,
	getConvertPositionBodyText,
	getConvertPositionButtonTitle,
	getNewProductType,
	getHoldingsSortData,
	getDeliveryT1TagConfig,
	deliveryT1InfoConfig,
	deliveryT1InfoConfigByQuantity,
	getProductConversionConfig,
	getMarginInfoPlayLoad,
	getConvertPositionPayload
};

export default stocksPortfolioService;
